import './styles.css'
import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Message,
} from 'semantic-ui-react'
import useSessionStorageState from '../../../../../../common-components/hooks/useSessionStorage'
import KegCollectionEditModal from '../KegCollectionEditModal/index'
import KegCollectionDeleteModal from '../KegCollectionDeleteModal/index'

const SESSION_KEYS = {
  sortKey: 'kegCollectionBookingsDetails.table.sortKey',
  sortDirection: 'kegCollectionBookingsDetails.table.sortDirection',
}

const KegCollectionRequestsTable = props => {
  const {
    formattedBookingDetails,
    dateFormatter,
    formatKegCollectionTableRows,
    formatDate,
    dispatch,
    createAction,
    translate,
  } = props

  const [storedSortKey, setStoredSortKey] = useSessionStorageState({
    key: SESSION_KEYS.sortKey,
    initialValue: 'bk_dv_tHeader_date_requested',
    version: '1',
  })

  const [storedSortDirection, setStoredSortDirection] = useSessionStorageState({
    key: SESSION_KEYS.sortDirection,
    initialValue: 'descending',
    version: '1',
  })

  const HEADERS = React.useMemo(
    () => [
      { key: 'bk_dv_tHeader_date_requested', label: translate('Date Requested'), isSortable: true },
      { key: 'bk_dv_tHeader_20L', label: '20L', isSortable: false },
      { key: 'bk_dv_tHeader_30L', label: '30L', isSortable: false },
      { key: 'bk_dv_tHeader_50L', label: '50L', isSortable: false },
      { key: 'bk_dv_tHeader_9G', label: '9G', isSortable: false },
      { key: 'bk_dv_tHeader_total_kegs', label: translate('totalKegs'), isSortable: false },
      { key: 'bk_dv_tHeader_actions', label: '', isSortable: false },
    ],
    [translate]
  )

  const kegCollectionsTableData = React.useMemo(() => {
    if (!formattedBookingDetails?.kegCollectionRequestObjects) return []
    return formatKegCollectionTableRows(
      formattedBookingDetails?.kegCollectionRequestObjects,
      dateFormatter
    )
  }, [formattedBookingDetails, dateFormatter, formatKegCollectionTableRows])

  const sortedData = React.useMemo(() => {
    if (!kegCollectionsTableData.length) return []
    const dataCopy = [...kegCollectionsTableData]
    dataCopy.sort((a, b) => {
      if (a._sort_dateRequested < b._sort_dateRequested) {
        return storedSortDirection === 'ascending' ? -1 : 1
      }
      if (a._sort_dateRequested > b._sort_dateRequested) {
        return storedSortDirection === 'ascending' ? 1 : -1
      }
      return 0
    })
    return dataCopy
  }, [kegCollectionsTableData, storedSortDirection])

  // eslint-disable-next-line prefer-arrow-callback
  const TableRowItem = React.memo(function TableRowItem({
    collectionId,
    dateRequested,
    L20,
    L30,
    G9,
    L50,
    total,
    kegCollectionObj,
  }) {
    return (
      <TableRow key={collectionId}>
        <TableCell>{dateRequested}</TableCell>
        <TableCell>{L20 || 0}</TableCell>
        <TableCell>{L30 || 0}</TableCell>
        <TableCell>{L50 || 0}</TableCell>
        <TableCell>{G9 || 0}</TableCell>
        <TableCell>{total || 0}</TableCell>
        <TableCell collapsing>
          <div
            style={{
              display: 'flex',
              gap: 10,
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <KegCollectionEditModal
              kegCollectionObj={kegCollectionObj}
              kegCollectionQuantities={{ L20, L30, G9, L50 }}
              formatDate={formatDate}
              dateFormatter={dateFormatter}
              dispatch={dispatch}
              createAction={createAction}
              translate={translate}
            />
            <KegCollectionDeleteModal
              kegCollectionObj={kegCollectionObj}
              kegCollectionQuantities={{ L20, L30, G9, L50 }}
              formatDate={formatDate}
              dateFormatter={dateFormatter}
              dispatch={dispatch}
              createAction={createAction}
              translate={translate}
            />
          </div>
        </TableCell>
      </TableRow>
    )
  })

  const handleSort = React.useCallback(
    key => {
      setStoredSortKey(key)
      setStoredSortDirection(prev => (prev === 'ascending' ? 'descending' : 'ascending'))
    },
    [setStoredSortDirection, setStoredSortKey]
  )

  return (
    <div className="animate-into-view-1">
      <Table sortable compact textAlign="center" className="sticky-header-table">
        <TableHeader>
          <TableRow>
            {HEADERS.map(({ key, label, isSortable }) => (
              <TableHeaderCell
                key={key}
                scope="col"
                className={
                  isSortable
                    ? 'kegCollectionsTableColIsSortable'
                    : 'kegCollectionsTableColNotSortable'
                }
                onClick={isSortable ? () => handleSort(key) : undefined}
                sorted={isSortable && storedSortKey === key ? storedSortDirection : null}
              >
                {label}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        {sortedData.length > 0 && (
          <TableBody>
            {sortedData.map(row => (
              <TableRowItem key={row.collectionId} {...row} />
            ))}
          </TableBody>
        )}
      </Table>

      {sortedData.length === 0 && <Message content={translate('noKegCollectionFoundForBooking')} />}
    </div>
  )
}

export default React.memo(KegCollectionRequestsTable)
